import React, { Component } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import _ from 'lodash';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/free-solid-svg-icons/index';
import { jobCreationSteps, jobWorkerStatus, privatePartyJobCategoryTitle } from '../../../../constants/jobs';
import { useTimezoneForDate } from '../../../../helpers/timezones';
import { getCostPayRate, getEstCostValue, getDollarPrice, getCentPrice, tryParseMonetaryValue } from '../../../../helpers/general';
import { modifyObject } from '../../../../helpers/modifyObject';
import { formatEventDate, formatPrice } from '../../../../helpers/formatData';
import { getDateIgnoringTimeAndTimezone } from '../../../../helpers/date';
import { appPath } from '../../../../constants';
import LocationScheduleStep from './LocationScheduleStep';
import PositionStep from './PositionStep';
import DressCodeDescriptionStep from './DressCodeDescriptionStep';
import SummaryStep from './SummaryStep';
import ShortSummary from './ShortSummary';
import ChooseEmployerEventStep from './ChooseEmployerEventStep';
import { GET_EMPLOYER_DETAILS_SUCCESS } from '../../../../actions/actionTypes';
import UsersService from '../../../../services/UsersService';
import { errorCodes } from '../../../../constants/errorCodes';
import { alertMessages, confirmationMessages } from '../../../../constants/text';
import { favStatuses } from '../../../../constants/workers';

export default class CreateJob extends Component {
  static propTypes = {
    user: PropTypes.object.isRequired,
    employersPredictionsList: PropTypes.array.isRequired,
    userEventsList: PropTypes.array.isRequired,
    history: PropTypes.object.isRequired,
    companyDetails: PropTypes.shape({
      ltaWorkers: PropTypes.array.isRequired,
      favAndBlocks: PropTypes.array.isRequired
    }),
    getCompanyLocations: PropTypes.func.isRequired,
    companyLocations: PropTypes.array.isRequired,
    locationsList: PropTypes.array.isRequired,
    jobCategories: PropTypes.array.isRequired,
    basicSkillLevelId: PropTypes.number,
    costPayRateList: PropTypes.array.isRequired,
    costPayRateIsLoading: PropTypes.bool.isRequired,
    dressCodesList: PropTypes.array.isRequired,
    addUserLocation: PropTypes.func.isRequired,
    getPayCostRateAction: PropTypes.func.isRequired,
    createMultiDayJob: PropTypes.func.isRequired,
    createJobAction: PropTypes.func.isRequired,
    postToBoardAction: PropTypes.func.isRequired,
    getCompanyLTAWorkers: PropTypes.func.isRequired,
    clearCompanyLTAWorkers: PropTypes.func.isRequired,
    getCompanyFavAndBlocks: PropTypes.func.isRequired,
    clearCompanyFavAndBlocks: PropTypes.func.isRequired,
    addJobWorkerAction: PropTypes.func.isRequired,
    getDetailedUser: PropTypes.func.isRequired,
    searchEmployersAction: PropTypes.func.isRequired,
    clearEmployersPredictionsList: PropTypes.func.isRequired,
    createEventAction: PropTypes.func.isRequired,
    editEventAction: PropTypes.func.isRequired
  };

  state = {
    currentStep: UsersService.isAdmin() ? jobCreationSteps.admin.id : jobCreationSteps.first.id,
    selectedEvent: null,
    selectedCategoryId: null,
    jobSkillData: null,
    selectedLocation: null,
    jobsList: [],
    jobCostRate: 0,
    workerPayRate: 0,
    checkin_verification_required: false,
    dress_code_id: 0,
    dress_code: '',
    description: '',
    where_to_report: '',
    isLoading: false
  };

  componentDidMount () {
    if (UsersService.isEmployer()) {
      this.getCompanyLocationsData(this.props.user.company_id);
      this.props.getCompanyDetailsAction(this.props.user.company_id);
    }
  }

  componentDidUpdate (prevProps) {
    const { companyDetails, jobCategories } = this.props;
    if (companyDetails.is_individual && jobCategories.length > 0 && !this.state.selectedCategoryId) {
      const privatePartyCategory = jobCategories.find(category => category.reference_title === privatePartyJobCategoryTitle);
      if (privatePartyCategory) this.chooseCategory(privatePartyCategory);
    }
  }

  componentWillUnmount () {
    this.props.clearCompanyLTAWorkers();
    this.props.clearCompanyFavAndBlocks();
  }

  chooseUser = employerData => {
    const { getEventsAction, getDetailedUser, getCompanyDetailsAction } = this.props;
    getDetailedUser(employerData.id, GET_EMPLOYER_DETAILS_SUCCESS).then(() => {
      const { user: { company_id, time_zone_id } } = this.props;
      getCompanyDetailsAction(company_id);
      getEventsAction(company_id, time_zone_id);
      this.getCompanyLocationsData(company_id);
    });
  };

  getCompanyLocationsData = company_id => {
    this.props.getCompanyLocations(company_id).then(() => {
      if (this.props.companyLocations.length === 1) {
        const locationData = this.props.companyLocations[0];
        const selectedLocation = {
          label: `${locationData.location_name}, ${locationData.address}`,
          value: locationData.id,
          data: locationData
        };
        this.handleSelectLocation(selectedLocation);
      }
    });
  };

  exitJobCreation = () =>
    this.props.UIShowConfirm(confirmationMessages.exitJobCreation, this.navToJobsList);

  goToStep = stepId => {
    this.setState({
      currentStep: stepId
    });
  };

  handleSelectEvent = selectedEventData => {
    const selectedEvent = selectedEventData && {
      ...selectedEventData,
      data: {
        ...selectedEventData.data,
        // Events list for user come from the server with dates in such format with timezone 'YYYY-MM-DDT00:00:00.000Z'.
        // We need to format this dates to 'YYYY-MM-DD' format to get date as it is in database.
        // We can't use moment for this, because we need to ignore timezones, so we just cut this date using substr.
        start_date: getDateIgnoringTimeAndTimezone(selectedEventData.data.start_date),
        end_date: getDateIgnoringTimeAndTimezone(selectedEventData.data.end_date)
      }
    };
    this.setState({ selectedEvent });
  };

  submitCreateEvent = eventData => {
    const { start_date, end_date } = eventData;
    eventData.start_date = formatEventDate(start_date);
    eventData.end_date = formatEventDate(end_date);
    if (UsersService.isAdmin()) {
      eventData.user_id = this.props.user.id;
    }
    return this.props.createEventAction(eventData).then(event => {
      this.handleSelectEvent({
        label: event.name,
        value: event.id,
        data: event
      });
    });
  };

  chooseCategory = selectedCategoryData => {
    if (selectedCategoryData) {
      this.setState({
        selectedCategoryId: selectedCategoryData.id
      }, () => this.handleChangeSkill(null));
    }
  };

  handleChangeSkill = jobSkillData => {
    const jobDescription = jobSkillData && jobSkillData.id ? jobSkillData.job_comments_template : '';
    const { getCompanyLTAWorkers, user: { company_id, is_lta }, getCompanyFavAndBlocks } = this.props;
    const isAdmin = UsersService.isAdmin();
    if (jobSkillData) {
      if (is_lta) {
        getCompanyLTAWorkers(isAdmin ? company_id : null, jobSkillData.id);
      } else {
        getCompanyFavAndBlocks({
          company_id: isAdmin ? company_id : null,
          status: favStatuses.favorite,
          skill: jobSkillData.id
        });
      }
    }
    this.setState({
      jobSkillData,
      description: jobDescription
    }, () => this.setCostPayRate());
  };

  handleSelectLocation = selectedLocation => {
    if (selectedLocation) {
      const { address, city, state, zip, coordinates } = selectedLocation.data;
      if (!address || !city || !state || !zip || !coordinates) {
        return this.props.UIShowAlert(alertMessages.invalidAddress);
      }
    }
    this.setState({ selectedLocation }, () => this.setCostPayRate().then(() => this.scrollToCalendar()));
  };

  addLocationToJob = locationData => {
    return this.props.addUserLocation(locationData).then(() => {
      this.setState({
        selectedLocation: {
          label: `${locationData.location_name}, ${locationData.address}`,
          value: locationData.id,
          data: locationData
        }
      }, () => this.setCostPayRate().then(() => this.scrollToCalendar()));
    });
  };

  setCalendarRef = calendarElement => {
    this.calendarRef = calendarElement;
  };

  scrollToCalendar = () => {
    if (this.calendarRef) {
      this.calendarRef.scrollIntoView({ behavior: 'smooth' });
    }
  };

  setCostPayRate = async () => {
    const { jobSkillData, selectedLocation } = this.state;
    const { company_id } = this.props.user;
    if (jobSkillData && jobSkillData.id && selectedLocation) {
      const data = {
        job_skill_id: jobSkillData.id,
        job_coordinates: selectedLocation.data.coordinates,
        state: selectedLocation.data.state,
        company_id
      };
      const { getPayCostRateAction, basicSkillLevelId } = this.props;
      return getPayCostRateAction(data).then(costPayRateList => {
        if (costPayRateList) {
          const jobCostRate = getDollarPrice(getCostPayRate('cost_rate', costPayRateList, basicSkillLevelId));
          const workerPayRate = getDollarPrice(getCostPayRate('pay_rate', costPayRateList, basicSkillLevelId));
          this.setState({
            jobCostRate,
            workerPayRate
          }, () => this.recalculateEstCostForJobs());
        }
      });
    }
  };

  recalculateEstCostForJobs = () => {
    const { jobsList, jobCostRate } = this.state;
    if (jobsList.length > 0) {
      jobsList.forEach(job => {
        job.estCost = getEstCostValue(jobCostRate, job.hours, job.quantityOfWorkers);
      });
      this.setJobsList(jobsList);
    }
  };

  handleChangeCostPayRate = event => {
    const { value, name } = event.target;
    const resolvedValue = tryParseMonetaryValue(value);
    if (!_.isNull(resolvedValue)) {
      this.setState({
        [name]: resolvedValue
      }, () => this.recalculateEstCostForJobs());
    }
  };

  setJobsList = jobsList => {
    this.setState({ jobsList });
  };

  handleChangeDressCode = dressCodeData => {
    this.setState({ dress_code_id: dressCodeData.id });
  };

  handleChangeTextInput = event => {
    const { name, value } = event.target;
    this.setState({
      [name]: value
    });
  };

  handleSwitch = name => this.setState(prevState => ({ [name]: !prevState[name] }));

  isFirstStepFilled = () => {
    const { jobSkillData } = this.state;
    return !!jobSkillData && !!jobSkillData.id;
  };

  isSecondStepFilled = () => {
    const { selectedLocation, jobsList, jobCostRate, workerPayRate } = this.state;
    return !!selectedLocation && !!selectedLocation.value && jobsList.length > 0 && jobCostRate && workerPayRate;
  };

  isThirdStepFilled = () => {
    return !!this.state.description && !!this.state.where_to_report;
  };

  getStepName = () => {
    const { currentStep } = this.state;
    switch (currentStep) {
      case jobCreationSteps.admin.id:
        return jobCreationSteps.admin.ui;
      case jobCreationSteps.first.id:
        return jobCreationSteps.first.ui;
      case jobCreationSteps.second.id:
        return jobCreationSteps.second.ui;
      case jobCreationSteps.third.id:
        return jobCreationSteps.third.ui;
      default:
        return '';
    }
  };

  calcSumHoursValue = () => {
    const { jobsList } = this.state;
    return jobsList.reduce((sum, job) => sum + job.hours, 0);
  };

  calcSumEstCost = () => {
    const { jobsList } = this.state;
    const estCostSum = jobsList.reduce((sum, job) => sum + job.estCost, 0);
    return formatPrice(estCostSum);
  };

  createJob = isPostToBoard => {
    const { jobsList, jobSkillData, dress_code_id, dress_code, description, where_to_report, jobCostRate, workerPayRate,
      selectedLocation, selectedEvent, checkin_verification_required } = this.state;
    const { address, state, city, zip, coordinates, location_name, job_location_type_id, time_zone_id } = selectedLocation.data;
    const user_id = UsersService.isAdmin() ? this.props.user.id : this.props.user.userid;
    this.setState({
      isLoading: true
    });

    let jobData = modifyObject({
      user_id,
      is_estimated_hours: true,
      job_skill_id: jobSkillData.id,
      skill_level_id: this.props.basicSkillLevelId,
      dress_code_id,
      dress_code,
      description,
      where_to_report,
      job_cost_rate: getCentPrice(jobCostRate),
      worker_pay_rate: getCentPrice(workerPayRate),
      address,
      state,
      city,
      zip,
      address_coordinates: coordinates,
      location_name,
      job_location_type_id,
      checkin_verification_required
    });

    if (selectedEvent) {
      const { id, end_date } = selectedEvent.data;
      jobData.event_id = id;
      const lastJobDate = jobsList[jobsList.length - 1].start.startOf('day');
      if (moment(end_date).isBefore(lastJobDate)) {
        const eventData = {
          end_date: formatEventDate(lastJobDate)
        };
        this.props.editEventAction(id, eventData);
      }
    }

    const isMultiDayJob = jobsList.length > 1;
    const { is_lta } = this.props.user;

    const jobs = jobsList.map(job => {
      const jobData = {
        start_date: moment.utc(useTimezoneForDate(job.startTime, time_zone_id)).format(),
        end_date: moment.utc(useTimezoneForDate(job.endTime, time_zone_id)).format(),
        hours: job.hours,
        quantity_of_workers: job.quantityOfWorkers
      };
      // send workers for lta and multi-day jobs (except save as draft when is_post_to_board: null)
      if ((isMultiDayJob && !_.isNull(isPostToBoard)) || is_lta) {
        jobData.workers = job.selectedWorkers.map(worker => ({
          id: worker.user_id,
          is_lta
        }));
      }
      return jobData;
    });

    if (isMultiDayJob || is_lta) {
      jobData.jobs = jobs;
      jobData.is_lta = is_lta;
      if (is_lta) {
        jobData.is_post_to_board = false;
      } else {
        jobData.is_post_to_board = isPostToBoard; // for multi-day non LTA
      }
      return this.props.createMultiDayJob(jobData);
    } else {
      jobData = Object.assign(jobs[0], jobData);
      return this.props.createJobAction(jobData);
    }
  };

  createJobLta = () => {
    this.createJob().then(jobData => {
      this.setDataLoaded();
      if (jobData && jobData.error && jobData.error.code === errorCodes.noCConFile) {
        this.handleNoCcError();
      } else if (jobData && jobData.event_id) {
        this.navToEventView(jobData.event_id);
      } else {
        this.navToJobsList();
      }
    });
  };

  saveJobAsDraft = () => {
    const { jobsList } = this.state;
    const selectedWorkersExist = jobsList.some(job => job.selectedWorkers.length > 0);
    if (selectedWorkersExist) {
      this.props.UIShowConfirm(confirmationMessages.saveDraftWithWorkers, this.saveJob);
    } else {
      this.saveJob();
    }
  };

  // Save as draft for non lta:
  saveJob = () => {
    this.createJob(null).then(jobData => {
      this.setDataLoaded();
      if (jobData && jobData.event_id) {
        this.navToEventView(jobData.event_id);
      } else {
        this.navToJobsList();
      }
    });
  };

  // For non lta:
  postJob = postToBoard => {
    this.createJob(postToBoard).then(jobData => {
      const isMultiDayJob = this.state.jobsList.length > 1;
      const { postToBoardAction, addJobWorkerAction } = this.props;
      // for multi-day route we send workers in payload and they become hired
      // for single job after job is created we need to send one more request to post job and add workers
      if (isMultiDayJob) {
        this.setDataLoaded();
        if (jobData && jobData.error && jobData.error.code === errorCodes.noCConFile) {
          this.handleNoCcError();
        } else if (jobData && jobData.event_id) {
          this.navToEventView(jobData.event_id);
        } else {
          this.navToJobsList();
        }
      } else {
        postToBoardAction([jobData.id], postToBoard).then(data => {
          if (data.error && data.error.code === errorCodes.noCConFile) {
            this.setDataLoaded();
            this.handleNoCcError();
          } else {
            const { selectedWorkers } = this.state.jobsList[0];
            const promises = selectedWorkers.map(worker => addJobWorkerAction({
              job_id: jobData.id,
              user_id: worker.user_id,
              status: jobWorkerStatus.OFFERED
            }));
            const redirectAfterJobPosting = () => {
              this.setDataLoaded();
              if (postToBoard) {
                this.navToJobsList();
              } else {
                this.navToJobDetails(jobData.id);
              }
            };
            Promise.all(promises).then(redirectAfterJobPosting).catch(redirectAfterJobPosting);
          }
        });
      }
    });
  };

  setDataLoaded = () => {
    this.setState({
      isLoading: false
    });
  };

  navToJobsList = () => {
    this.props.history.push(`/${appPath}/jobs`);
  };

  navToEventView = event_id => {
    this.props.history.push(`/${appPath}/events/${event_id}`, { cantReturnBack: true });
  };

  navToJobDetails = jobId => {
    this.props.history.push(`/${appPath}/jobs/${jobId}`, { switchToWorkers: true, cantReturnBack: true });
  };

  handleNoCcError = () => {
    this.navToJobsList();
    this.props.UIShowAlert(alertMessages.noCConFileJobCreation[UsersService.getUserRole()]);
  };

  renderSteps = () => {
    const { currentStep, selectedLocation, selectedCategoryId, jobSkillData, dress_code_id, dress_code, description,
      where_to_report, isLoading, jobsList, jobCostRate, workerPayRate, selectedEvent, checkin_verification_required } = this.state;
    const jobSkillId = jobSkillData ? jobSkillData.id : null;
    const dressCodeLabel = dress_code_id ? this.props.dressCodesList.find(listItem => listItem.id === dress_code_id).description_en : '';
    const isLta = this.props.user.is_lta;
    const workersList = isLta ? this.props.companyDetails.ltaWorkers : this.props.companyDetails.favAndBlocks;
    const selectedWorkersInfoIsShown = workersList.length > 0;
    switch (currentStep) {
      case jobCreationSteps.admin.id:
        const { user, employersPredictionsList, searchEmployersAction, clearEmployersPredictionsList, userEventsList } = this.props;
        return (
          <ChooseEmployerEventStep
            user={user}
            employersPredictionsList={employersPredictionsList}
            searchEmployersAction={searchEmployersAction}
            clearEmployersPredictionsList={clearEmployersPredictionsList}
            selectedEvent={selectedEvent}
            chooseUser={this.chooseUser}
            goToStep={this.goToStep}
            handleSelectEvent={this.handleSelectEvent}
            userEventsList={userEventsList}
            submitCreateEvent={this.submitCreateEvent}
          />
        );
      case jobCreationSteps.first.id:
        return (
          <PositionStep
            isPrivateParty={this.props.companyDetails.is_individual}
            jobCategories={this.props.jobCategories}
            selectedCategoryId={selectedCategoryId}
            jobSkillId={jobSkillId}
            chooseCategory={this.chooseCategory}
            handleChangeSkill={this.handleChangeSkill}
            isFirstStepFilled={this.isFirstStepFilled}
            goToStep={this.goToStep}
          />
        );
      case jobCreationSteps.second.id:
        return (
          <LocationScheduleStep
            companyDetails={this.props.companyDetails}
            isLta={isLta}
            selectedWorkersInfoIsShown={selectedWorkersInfoIsShown}
            selectedLocation={selectedLocation}
            companyLocations={this.props.companyLocations}
            locationsList={this.props.locationsList}
            handleSelectLocation={this.handleSelectLocation}
            addLocationToJob={this.addLocationToJob}
            costPayRateList={this.props.costPayRateList}
            costPayRateIsLoading={this.props.costPayRateIsLoading}
            jobsList={jobsList}
            jobCostRate={jobCostRate}
            workerPayRate={workerPayRate}
            handleChangeCostPayRate={this.handleChangeCostPayRate}
            setJobsList={this.setJobsList}
            isSecondStepFilled={this.isSecondStepFilled}
            goToStep={this.goToStep}
            setCalendarRef={this.setCalendarRef}
            selectedEventData={selectedEvent && selectedEvent.data}
          />
        );

      case jobCreationSteps.third.id:
        return (
          <DressCodeDescriptionStep
            company={this.props.companyDetails}
            dressCodesList={this.props.dressCodesList}
            dressCodeId={dress_code_id}
            dressCode={dress_code}
            description={description}
            whereToReport={where_to_report}
            checkin_verification_required={checkin_verification_required}
            handleSwitch={this.handleSwitch}
            handleChangeDressCode={this.handleChangeDressCode}
            handleChangeTextInput={this.handleChangeTextInput}
            isThirdStepFilled={this.isThirdStepFilled}
            goToStep={this.goToStep}
          />
        );
      default:
        return (
          <SummaryStep
            isLta={isLta}
            jobSkillData={jobSkillData}
            locationLabel={selectedLocation.label}
            description={description}
            jobsList={jobsList}
            hoursSum={this.calcSumHoursValue()}
            estCostSum={this.calcSumEstCost()}
            dressCodeLabel={dressCodeLabel}
            dressCodeInstructions={dress_code}
            whereToReport={where_to_report}
            goToStep={this.goToStep}
            createJobLta={this.createJobLta}
            saveJobAsDraft={this.saveJobAsDraft}
            postJob={this.postJob}
            isLoading={isLoading}
            selectedWorkersInfoIsShown={selectedWorkersInfoIsShown}
          />
        );
    }
  };

  render () {
    const { currentStep, selectedLocation, jobsList, jobSkillData, description } = this.state;
    const isShortSummaryShown = this.isFirstStepFilled() && this.isSecondStepFilled() && currentStep !== jobCreationSteps.summary.id;
    return (
      <div className='create-job-wrapper'>
        <div className='top-wrapper'>
          <h3>Post a Job</h3>
          <button
            className='close-button'
            onClick={this.exitJobCreation}
          ><FontAwesomeIcon icon={faTimes} size='xs' /></button>
        </div>
        { this.getStepName() && (
          <div className='tabs-wrapper'>
            <div className='tab active'>{ this.getStepName() }</div>
          </div>
        )}
        <div className='columns-wrapper'>
          <div className='steps-first-column'>
            { this.renderSteps() }
          </div>
          <section className='job-details-sum-main-wrapper'>
            { isShortSummaryShown && (
              <ShortSummary
                isSecondStepFilled={this.isSecondStepFilled}
                isThirdStepFilled={this.isThirdStepFilled}
                jobSkillData={jobSkillData}
                description={description}
                locationLabel={selectedLocation.label}
                jobsList={jobsList}
                hoursSum={this.calcSumHoursValue()}
                estCostSum={this.calcSumEstCost()}
              />
            )}
          </section>
        </div>
      </div>
    );
  }
}



// WEBPACK FOOTER //
// ./src/components/app/createJob/createJob/index.js